import React, { useEffect, useRef, useState } from "react";

import * as d3 from "d3";
import numeral from "numeral";

const RetirementChart = ({ height, chartData }) => {
  const {
    currentPortfolio,
    currentAge,
    retirementAge,
    yearsLeftInRetirement,
    annualContributions,
    accountSizeAtRetirement,
    runOutOfMoneyAtAge,
    willRunOutOfMoney,
    yearsInRetirement,
  } = chartData;
  const chartRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 650, height });
  const Sidebar = localStorage.getItem("Sidebar");
  const averageSavingBefore = (currentPortfolio + accountSizeAtRetirement) / 2;
  const averageAgeBefore = (currentAge + retirementAge) / 2;
  const checkAge = [true, false]?.includes(willRunOutOfMoney);
  const updatedAge = checkAge
    ? runOutOfMoneyAtAge
    : retirementAge + yearsInRetirement;
  const averageAgeAfter = (retirementAge + updatedAge) / 2;
  const updatedSaving = checkAge ? 0 : accountSizeAtRetirement * 0.2;
  const lastXScaleValue = checkAge ? updatedAge + 6 : updatedAge - 3;
  const beforeRetirement = [
    { age: currentAge, savings: currentPortfolio, color: "blue" },
    {
      age: averageAgeBefore,
      savings: averageSavingBefore - averageSavingBefore * 0.2,
      color: "blue",
    },
    { age: retirementAge, savings: accountSizeAtRetirement, color: "blue" },
  ];
  const afterRetirement = [
    { age: retirementAge, savings: accountSizeAtRetirement, color: "red" },
    {
      age: averageAgeAfter,
      savings: accountSizeAtRetirement,
      color: "red",
    },
    { age: updatedAge, savings: updatedSaving, color: "red" },
  ];
  const roundToNearest = (value) => {
    const num = Number(value);
    const orderOfMagnitude = 10 ** Math.floor(Math.log10(num));
    const roundedValue = Math.ceil(num / orderOfMagnitude) * orderOfMagnitude;
    return roundedValue;
  };

  const barLastPoint = roundToNearest(accountSizeAtRetirement);
  const RetirementAgeData = [
    { age: currentAge, savings: currentPortfolio, color: "blue" },
    {
      age: retirementAge,
      savings: yearsLeftInRetirement * annualContributions + currentPortfolio,
      color: "red",
    },
  ];

  const margin = { top: 20, right: 30, bottom: 30, left: 60 };

  // Dimensions of the chart
  useEffect(() => {
    const handleResize = () => {
      const newWidth = chartRef.current.parentElement.offsetWidth - 48;
      const newHeight = height;
      setDimensions({ width: newWidth, height: newHeight });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [height, Sidebar]);

  useEffect(() => {
    // Create SVG container
    d3.select(chartRef.current).selectAll("*").remove();
    const svg = d3
      .select(chartRef.current)
      .attr("width", dimensions.width)
      .attr("height", dimensions.height);

    // Scales
    const xScale = d3
      .scaleLinear()
      .domain([currentAge, lastXScaleValue])
      .range([margin.left, dimensions.width - margin.right]);

    const yScale = d3
      .scaleLinear()
      .domain([0, barLastPoint])
      .range([dimensions.height - margin.bottom, margin.top]);

    const lineGenerator = d3
      .line()
      .x((d) => xScale(d.age))
      .y((d) => yScale(d.savings))
      .curve(d3.curveBasis);

    const drawCurvedLine = (segment) => {
      svg
        .append("path")
        .datum(segment)
        .attr("d", lineGenerator)
        .attr("fill", "none")
        .attr("stroke", segment[0].color)
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "5,5");
    };

    // Draw the lines with dynamic stroke colors
    drawCurvedLine(beforeRetirement);
    drawCurvedLine(afterRetirement);

    // Draw the line
    svg
      .append("path")
      .datum(RetirementAgeData)
      .attr("d", lineGenerator)
      .attr("fill", "green")
      .attr("stroke", "#52C41A")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "5,5");

    svg
      .selectAll(".dot")
      .data([afterRetirement[0]])
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d.age))
      .attr("cy", (d) => yScale(d.savings))
      .attr("r", 6)
      .attr("fill", "white")
      .attr("stroke", "#003091")
      .attr("stroke-width", 2);

    // Draw the vertical line at age 60
    const yValue = yScale(accountSizeAtRetirement);
    const circleRadius = 6;

    svg
      .append("line")
      .attr("x1", xScale(retirementAge))
      .attr("x2", xScale(retirementAge))
      .attr("y1", yValue + circleRadius + 2)
      .attr("y2", dimensions.height - margin.bottom)
      .attr("stroke", "#BFBFBF")
      .attr("stroke-width", 1);

    // X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${dimensions.height - margin.bottom})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(5)
          .tickFormat((d) => `Age ${d}`),
      )
      .attr("class", "x-axis-line")
      .style("font-size", "14px")
      .style("color", "#BFBFBF");

    // Y Axis
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickFormat((d) => `$${numeral(d).format("0a").toUpperCase()}`),
      )
      .attr("class", "x-axis-line")
      .style("font-size", "14px")
      .style("color", "#BFBFBF");
  }, [dimensions, chartData]);

  return <svg ref={chartRef}></svg>;
};

export default RetirementChart;
